import { useState } from "react";
import { useWorkforceFilter, WorkforceViewMode } from "@/contexts/WorkforceFilterContext";
import { useWorkforcePaginated, WorkforceFilters } from "@/hooks/workforce/useWorkforcePaginated";
import { useAuth } from "@/providers/AuthProvider";
import { WorkforceCard } from "./WorkforceCard";
import { WorkforceList } from "./WorkforceList";
import { WorkforceKanban } from "./WorkforceKanban";
import { WorkforcePagination } from "./WorkforcePagination";
import { WorkforceEditModal } from "./WorkforceEditModal";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

interface WorkforceContentProps {
	onRefresh?: () => void;
}

export const WorkforceContent = ({ onRefresh }: WorkforceContentProps) => {
	const { t } = useTranslation();
	const { user } = useAuth();
	const {
		searchTerm,
		statusFilter,
		assignedToMe,
		dateRangeStart,
		dateRangeEnd,
		endingSoon,
		sortField,
		sortOrder,
		viewMode,
		currentPage,
		setCurrentPage
	} = useWorkforceFilter();

	const [editingId, setEditingId] = useState<string | null>(null);

	const filters: WorkforceFilters = {
		searchTerm,
		statusFilter,
		assignedToMe,
		currentUserId: user?.id || null,
		dateRangeStart: dateRangeStart || undefined,
		dateRangeEnd: dateRangeEnd || undefined,
		endingSoon: endingSoon ?? undefined
	};

	const {
		workforce,
		isLoading,
		totalCount,
		totalPages,
		refetch
	} = useWorkforcePaginated(filters, sortField, sortOrder);

	const handleRefetch = async () => {
		await refetch();
		if (onRefresh) onRefresh();
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-12">
				<Loader2 className="h-8 w-8 animate-spin text-primary" />
			</div>
		);
	}

	if (workforce.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center py-12 text-center">
				<p className="text-lg text-muted-foreground mb-2">
					{t("workforce.noResults", "No workforce assignments found")}
				</p>
				<p className="text-sm text-muted-foreground">
					{t("workforce.noResultsHint", "Try adjusting your filters or add a new assignment")}
				</p>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{/* Results count */}
			<div className="text-sm text-muted-foreground">
				{t("workforce.showing", "Showing")} {workforce.length} {t("workforce.of", "of")} {totalCount} {t("workforce.assignments", "assignments")}
			</div>

			{/* View content based on mode */}
			{viewMode === 'kanban' ? (
				<WorkforceKanban workforce={workforce} onStatusChange={handleRefetch} />
			) : viewMode === 'list' ? (
				<WorkforceList workforce={workforce} />
			) : (
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
					{workforce.map((item) => (
						<WorkforceCard key={item.id} workforce={item} onEdit={setEditingId} />
					))}
				</div>
			)}

			{/* Edit modal for card view */}
			{viewMode === 'card' && (
				<WorkforceEditModal
					workforceId={editingId}
					onOpenChange={(open) => { if (!open) setEditingId(null); }}
					onSuccess={handleRefetch}
				/>
			)}

			{/* Pagination - only for card and list views */}
			{viewMode !== 'kanban' && totalPages > 1 && (
				<WorkforcePagination
					currentPage={currentPage}
					totalPages={totalPages}
					onPageChange={setCurrentPage}
				/>
			)}
		</div>
	);
};
